import { Models } from "@/api/types";

export interface ListingPriceInput {
  basePriceCents: number;
  membershipPriceCents?: number | null;
  // precondition: discounts sent apply to this item
  discount?: Models<"ShopProductDiscountModel"> | null;
}

type DiscountDetail =
  | {
      id: string;
      type: "percentage";
      percentageOff: number;
    }
  | {
      id: string;
      type: "amount";
      amountOff: number;
    };

interface AppliedDiscountDetail {
  id: string;
  detail: DiscountDetail;
  appliedDiscountPriceCents: number;
}

export interface ListingPriceOutput {
  // Base values
  basePriceCents: number;
  membershipPriceCents: number | null;

  // Best price WITHOUT membership
  bestPriceWithoutMembership: number;
  bestPriceWithoutMembershipSource: "base" | "discount";
  appliedDiscountToBase?: string; // ID referencing applicableDiscounts array

  // Best price WITH membership
  bestPriceWithMembership: number;
  bestPriceWithMembershipSource: "membership" | "discount" | "base";
  appliedDiscountToMembership?: string; // ID referencing applicableDiscounts array

  // All applicable discounts with their computed prices
  applicableDiscounts: Record<string, AppliedDiscountDetail>;
}

export const computeListingBestPriceCents = (
  params: ListingPriceInput
): ListingPriceOutput => {
  const base = Math.max(0, params.basePriceCents ?? 0);
  const membership =
    typeof params.membershipPriceCents === "number"
      ? Math.max(0, params.membershipPriceCents)
      : null;
  // percentage/amount discount on base, if provided and applicable
  let discountedBase = base;
  const d = params.discount;
  if (d) {
    if (d.isVariable) {
      // Non-deterministic on list pages: do not change numeric price
    } else {
      const rawPct =
        typeof d.maxPercentageOff === "number" ? d.maxPercentageOff : 0;
      const pct = Math.max(0, Math.min(1, rawPct));
      const percentOffCents = Math.round(base * pct);

      const rawAmt = typeof d.maxAmountOff === "number" ? d.maxAmountOff : 0;
      const amountOffCents = Math.max(0, rawAmt);

      const candidatePct = Math.max(0, base - percentOffCents);
      const candidateAmt = Math.max(0, base - amountOffCents);
      discountedBase = Math.min(candidatePct, candidateAmt);
    }
  }

  const bestPriceWithoutMembership =
    discountedBase < base ? discountedBase : base;
  const bestPriceWithoutMembershipSource: "base" | "discount" =
    discountedBase < base ? "discount" : "base";

  let bestPriceWithMembership = discountedBase;
  let bestPriceWithMembershipSource: "membership" | "discount" | "base" =
    discountedBase < base ? "discount" : "base";

  if (typeof membership === "number") {
    if (membership <= discountedBase) {
      bestPriceWithMembership = membership;
      bestPriceWithMembershipSource = "membership";
    } else {
      bestPriceWithMembership = discountedBase;
      bestPriceWithMembershipSource =
        discountedBase < base ? "discount" : "base";
    }
  }

  // Build applicable discounts map (only deterministic ones)
  const applicableDiscounts: Record<string, AppliedDiscountDetail> = {};
  let appliedDiscountToBase: string | undefined = undefined;
  if (d && !d.isVariable) {
    const rawPct =
      typeof d.maxPercentageOff === "number" ? d.maxPercentageOff : 0;
    const pct = Math.max(0, Math.min(1, rawPct));
    const percentOffCents = Math.round(base * pct);
    const rawAmt = typeof d.maxAmountOff === "number" ? d.maxAmountOff : 0;
    const amountOffCents = Math.max(0, rawAmt);

    const candidatePct = Math.max(0, base - percentOffCents);
    const candidateAmt = Math.max(0, base - amountOffCents);

    if (pct > 0) {
      applicableDiscounts["percent"] = {
        id: "percent",
        detail: { id: "percent", type: "percentage", percentageOff: pct },
        appliedDiscountPriceCents: candidatePct,
      };
    }
    if (amountOffCents > 0) {
      applicableDiscounts["amount"] = {
        id: "amount",
        detail: { id: "amount", type: "amount", amountOff: amountOffCents },
        appliedDiscountPriceCents: candidateAmt,
      };
    }

    if (discountedBase < base) {
      if (applicableDiscounts["percent"] && applicableDiscounts["amount"]) {
        appliedDiscountToBase =
          applicableDiscounts["percent"].appliedDiscountPriceCents <=
          applicableDiscounts["amount"].appliedDiscountPriceCents
            ? "percent"
            : "amount";
      } else if (applicableDiscounts["percent"]) {
        appliedDiscountToBase = "percent";
      } else if (applicableDiscounts["amount"]) {
        appliedDiscountToBase = "amount";
      }
    }
  }

  return {
    basePriceCents: base,
    membershipPriceCents: membership,
    bestPriceWithoutMembership,
    bestPriceWithoutMembershipSource,
    bestPriceWithMembership,
    // cast because the type union includes "discount" but we only set
    // "membership" or "base" until discounts are implemented
    bestPriceWithMembershipSource:
      bestPriceWithMembershipSource as ListingPriceOutput["bestPriceWithMembershipSource"],
    applicableDiscounts,
    appliedDiscountToBase,
  };
};

export interface MembershipPriceInput {
  basePriceCents: number;
  discount?: Models<"ShopProductDiscountModel"> | null;
  deterministicOnly?: boolean;
}

export interface MembershipPriceOutput {
  basePriceCents: number;
  bestPriceCents: number;
  bestPriceSource: "base" | "discount";
  applicableDiscounts: Record<string, AppliedDiscountDetail>;
  appliedDiscountToBase?: "percent" | "amount";
}

export const computeMembershipBestPriceCents = (
  params: MembershipPriceInput
): MembershipPriceOutput => {
  const base = Math.max(0, params.basePriceCents ?? 0);
  const d = params.discount ?? null;

  // Ignore BXGY/variable for now (no price change)
  if (d?.isBuyXGetY || d?.isVariable) {
    return {
      basePriceCents: base,
      bestPriceCents: base,
      bestPriceSource: "base",
      applicableDiscounts: {},
    };
  }

  // Deterministic percent/amount
  let discounted = base;
  const applicableDiscounts: Record<string, AppliedDiscountDetail> = {};
  let appliedDiscountToBase: "percent" | "amount" | undefined = undefined;

  if (d && !d.isVariable && !d.isBuyXGetY) {
    const rawPct =
      typeof d.maxPercentageOff === "number" ? d.maxPercentageOff : 0;
    const pct = Math.max(0, Math.min(1, rawPct));
    const percentOffCents = Math.round(base * pct);

    const rawAmt = typeof d.maxAmountOff === "number" ? d.maxAmountOff : 0;
    const amountOffCents = Math.max(0, rawAmt);

    const candidatePct = Math.max(0, base - percentOffCents);
    const candidateAmt = Math.max(0, base - amountOffCents);

    if (pct > 0) {
      applicableDiscounts["percent"] = {
        id: "percent",
        detail: { id: "percent", type: "percentage", percentageOff: pct },
        appliedDiscountPriceCents: candidatePct,
      };
    }
    if (amountOffCents > 0) {
      applicableDiscounts["amount"] = {
        id: "amount",
        detail: { id: "amount", type: "amount", amountOff: amountOffCents },
        appliedDiscountPriceCents: candidateAmt,
      };
    }

    discounted = Math.min(candidatePct, candidateAmt);

    if (discounted < base) {
      if (applicableDiscounts["percent"] && applicableDiscounts["amount"]) {
        appliedDiscountToBase =
          applicableDiscounts["percent"].appliedDiscountPriceCents <=
          applicableDiscounts["amount"].appliedDiscountPriceCents
            ? "percent"
            : "amount";
      } else if (applicableDiscounts["percent"]) {
        appliedDiscountToBase = "percent";
      } else if (applicableDiscounts["amount"]) {
        appliedDiscountToBase = "amount";
      }
    }
  }

  let bestPriceSource: "base" | "discount" = "base";
  if (discounted < base) {
    bestPriceSource = "discount";
  }

  return {
    basePriceCents: base,
    bestPriceCents: discounted,
    bestPriceSource,
    applicableDiscounts,
    appliedDiscountToBase,
  };
};
